import { useAuth } from '@/features/auth/useAuth';
import { useDiscountRequestsList } from '@/features/discountRequests/api';
import { useOfferDeletionRequestsList } from '@/features/offerDeletionRequests/api';
import { cn } from '@/lib/cn';
import { isAdminRole } from '@coreltb/shared/constants';
import { ChevronRight } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { type NavItem, isExactMatchItem, visibleNavItems } from './navigation';

/** Nawigacja wspolna dla sidebaru i menu mobilnego (jedna lista, jeden wyglad). */
export function NavList({ touch = false }: { touch?: boolean }) {
  const { user } = useAuth();
  const items = visibleNavItems(user?.role);
  const mainItems = items.filter((i) => i.section === 'main');
  const adminItems = items.filter((i) => i.section === 'admin');

  return (
    <nav aria-label="Główna nawigacja" className="flex flex-col gap-6">
      <NavGroup label="Aplikacja">
        {mainItems.map((item) => (
          <NavItemLink key={item.to} item={item} touch={touch} />
        ))}
      </NavGroup>
      {adminItems.length > 0 && (
        <NavGroup label="Administracja">
          {adminItems.map((item) => (
            <NavItemLink key={item.to} item={item} touch={touch} />
          ))}
        </NavGroup>
      )}
    </nav>
  );
}

function NavGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 px-3 text-xs font-semibold text-fg-subtle">{label}</div>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </div>
  );
}

/** Pozycja podświetlona też na zakładkach swojej sekcji (np. Ustawienia → Cennik). */
function onSectionPath(item: NavItem, pathname: string): boolean {
  return (item.activeFor ?? []).some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function NavItemLink({ item, touch }: { item: NavItem; touch: boolean }) {
  const { pathname } = useLocation();
  const section = onSectionPath(item, pathname);
  return (
    <li>
      <NavLink
        to={item.to}
        end={isExactMatchItem(item)}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors',
            touch ? 'min-h-touch' : 'min-h-9',
            isActive || section
              ? 'bg-sidebar-item-active text-sidebar-fg-active before:absolute before:inset-y-2 before:-left-3 before:w-[3px] before:rounded-r-full before:bg-accent'
              : 'text-sidebar-fg hover:bg-sidebar-item-hover hover:text-fg',
          )
        }
      >
        {({ isActive }) => (
          <>
            <item.icon
              className={cn(
                'h-[18px] w-[18px] shrink-0 transition-colors',
                isActive || section ? 'text-accent' : 'text-fg-muted group-hover:text-fg',
              )}
              strokeWidth={2}
            />
            <span className="truncate">{item.label}</span>
            <NavItemTrailing item={item} />
          </>
        )}
      </NavLink>
    </li>
  );
}

function NavItemTrailing({ item }: { item: NavItem }) {
  if (item.badge) {
    return (
      <span className="ml-auto rounded-md bg-accent-subtle px-2 text-xs font-medium leading-5 text-accent">
        {item.badge}
      </span>
    );
  }
  if (item.pendingCounter) return <PendingCounter />;
  return null;
}

/** Oczekujące prośby (rabat + usunięcie oferty), polling 30 s w hookach. Admin/dyrektor. */
function PendingCounter({ className = 'ml-auto' }: { className?: string }) {
  const { user } = useAuth();
  const canSee = Boolean(user) && (isAdminRole(user?.role) || user?.role === 'dyrektor_sprzedazy');
  const discounts = useDiscountRequestsList('pending');
  const deletions = useOfferDeletionRequestsList('pending');
  const count = canSee ? (discounts.data?.length ?? 0) + (deletions.data?.length ?? 0) : 0;
  if (count === 0) return null;
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-warning px-1.5 text-xs font-bold text-bg',
        className,
      )}
      title={`Czeka na decyzję: ${count}`}
    >
      {count}
    </span>
  );
}

/**
 * Menu na telefonie — styl „lista z dolu” (Dawid 2026-09-30 wybral wariant A):
 * grupy wierszy z ikona, licznikiem i strzalka, jak ustawienia w iPhonie.
 * Ta sama lista i uprawnienia co sidebar. `query` filtruje po nazwie (bez ogonkow).
 */
export function NavSheetList({ query }: { query: string }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const q = normalize(query.trim());
  const items = visibleNavItems(user?.role).filter((i) => !q || normalize(i.label).includes(q));
  const groups = [
    { label: 'Aplikacja', items: items.filter((i) => i.section === 'main') },
    { label: 'Administracja', items: items.filter((i) => i.section === 'admin') },
  ].filter((g) => g.items.length > 0);

  if (groups.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-fg-muted">Nic nie pasuje do „{query.trim()}”</p>
    );
  }

  return (
    <nav aria-label="Menu" className="space-y-5">
      {groups.map((g) => (
        <div key={g.label}>
          <div className="mb-1.5 px-1 text-sm font-semibold text-fg-muted">{g.label}</div>
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface shadow-card">
            {g.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={isExactMatchItem(item)}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-[52px] items-center gap-3 px-3.5 transition-colors active:bg-surface-2',
                      (isActive || onSectionPath(item, pathname)) && 'bg-accent-subtle',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                          isActive || onSectionPath(item, pathname)
                            ? 'bg-primary text-primary-fg'
                            : 'bg-accent-subtle text-accent',
                        )}
                      >
                        <item.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-fg">
                        {item.label}
                      </span>
                      {item.badge && (
                        <span className="rounded-md bg-surface-2 px-1.5 text-xs font-medium text-fg-muted">
                          {item.badge}
                        </span>
                      )}
                      {item.pendingCounter && <PendingCounter />}
                      <ChevronRight className="h-4 w-4 shrink-0 text-fg-subtle" strokeWidth={2} />
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/** Male litery bez polskich znakow — „prosby” znajdzie „Prośby o rabat”. */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/ł/g, 'l');
}
